import { useEffect, useState } from 'react';

import {
  fetchPostIdCheck,
  fetchPostOnfidoCheck,
  GetStateResponse,
} from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';

import entrust_logo from './entrust.png';
import { TIDCheckResolutionMethod } from './IDCheck';
import {
  IDCheckPendingModal,
  ID_CHECK_PENDING_MODAL,
} from './IDCheckPendingModal';
import idverse_logo from './idverse.png';
import { PhoneNumberForm } from './PhoneNumberForm';
import { TooManyAttempts } from './TooManyAttempts';
import { TGetFundingRequirementsResponse } from '../../../../api/lending/edge';
import AlertIcon from '../../../../components/svg/AlertIcon.svg?react';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { RecursiveNonNullable } from '../../../../utils/typeUtils';
import { Accordion } from '../../../components/Accordion/Accordion';
import { Card } from '../../../components/Card/Card';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useIsOverlay } from '../../../hooks/useIsOverlay';
import { useModal } from '../../../store/ModalProvider';
import { useCurrentOfferId } from '../../hooks/useCurrentOfferId';

export type CustomerPhone =
  RecursiveNonNullable<GetStateResponse>['data']['application']['people'][number]['phones'][number];

export type CustomerStatus =
  RecursiveNonNullable<TGetFundingRequirementsResponse>['data'][0]['resolution_methods'][0]['items'][0]['status'];

export const IDCheckNotComplete = ({
  applicantStatus,
  applicantUID,
  resolution_method,
}: {
  applicantStatus: CustomerStatus;
  applicantUID: string | undefined;
  resolution_method: TIDCheckResolutionMethod;
}) => {
  const { stateKey } = useStateKey();
  const { openModal } = useModal(ID_CHECK_PENDING_MODAL);
  const [isTooManyAttempts, setIsTooManyAttempts] = useState(false);
  const isDesktop = useMediaQuery('(min-width:600px)');
  const isOverlay = useIsOverlay();
  const currentOfferId = useCurrentOfferId();

  const [isForceShowOnfidoSMS, setIsForceShowOnfidoSMS] = useState(false);

  /*
    If Onfido:
      - show SMS if desktop, not mobile
    If IDVers:
      - show SMS regardless of desktop or mobile
  */
  const showSMSInput =
    resolution_method === 'idverse_check'
      ? true
      : isDesktop || isForceShowOnfidoSMS || isOverlay;

  const submitButtonText =
    applicantStatus === 'error' ? 'Try again' : 'Go to ID check';

  useEffect(() => {
    if (applicantStatus === 'pending') {
      openModal();
    }
  }, [applicantStatus, openModal]);

  const redirectToIDCheck = async () => {
    let verifyUrl: string | undefined;
    try {
      if (resolution_method === 'idverse_check') {
        const data = await fetchPostIdCheck({
          stateKey: stateKey!,
          body: {
            data: {
              offer_id: currentOfferId!,
              person_uid: applicantUID!,
              redirect_url: window.location.href,
            },
          },
        });
        // @ts-expect-error - types are incorrect
        verifyUrl = data.data.verify_url;
      } else {
        const data = await fetchPostOnfidoCheck({
          stateKey: stateKey!,
          personUid: applicantUID!,
        });
        verifyUrl = data.data.verify_url;
      }
      openModal();
      if (verifyUrl) {
        window.open(verifyUrl);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setIsTooManyAttempts(true);
    }
  };

  if (isTooManyAttempts) {
    return <TooManyAttempts />;
  }

  return (
    <Card title="ID check">
      <IDCheckPendingModal
        onClickForceShowOnfidoSMS={() => setIsForceShowOnfidoSMS(true)}
        showOnfidoSMSInput={showSMSInput}
      />
      <Accordion title="We need to confirm it's really you">
        <div className="mt-l block">
          We just need to do a quick and simple ID check to check you are who
          you say you are. It stops fraudsters paying for things under your
          name.
        </div>
      </Accordion>
      <div className="mb-l mt-xl overflow-auto text-m font-reg">
        We'll need you to take a photo of <b>one</b> of the <br /> following
        documents:
        <ul className="mb-0 mt-l">
          <li>Passport</li>
          <li>Driving license</li>
        </ul>
      </div>
      {applicantStatus === 'error' && (
        <div className="mb-l flex gap-r rounded-m bg-core-95 py-r pl-r pr-l text-m font-reg">
          <AlertIcon className="mt-2xs overflow-visible [&_path]:fill-error" />
          <div>
            <p className="mb-l">
              Seems something wasn't quite right with your ID check. Please try
              again.
            </p>
            <p className="m-0">
              Make sure the corners are aligned in the capture area, that you're
              in a well-lit environment and that the picture is clearly visible.
            </p>
          </div>
        </div>
      )}
      {!showSMSInput ? (
        <div>
          <WhiteLabelOverride componentType="button" disabled={false}>
            <Button
              variant="primary"
              className="w-full"
              onClick={redirectToIDCheck}
            >
              {submitButtonText}
            </Button>
          </WhiteLabelOverride>
          <div className="mt-l flex items-center justify-center text-xs font-med text-structure-40">
            Secured and powered by{' '}
            {resolution_method === 'idverse_check' ? (
              <img
                src={idverse_logo}
                alt="idverse logo"
                className="ml-s h-[20px]"
              />
            ) : (
              <img
                src={entrust_logo}
                alt="Entrust logo"
                className="ml-s h-[20px]"
              />
            )}
          </div>
        </div>
      ) : (
        <div>
          <PhoneNumberForm
            applicantUID={applicantUID}
            applicantStatus={applicantStatus}
            onError={() => setIsTooManyAttempts(true)}
            resolution_method={resolution_method}
          />
        </div>
      )}
    </Card>
  );
};
